import React, {
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useState,
} from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { FormInputDate, Stack, P } from '@fashionnovateam/nova-ui-toolkit';
import { AddTagFormData } from '../types';

function SmartPositionedFormInputDate(
  props: React.ComponentProps<typeof FormInputDate>,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<'bottom' | 'top'>('bottom');

  const calculateOptimalPosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    const requiredSpace = 340;

    if (spaceBelow >= requiredSpace) {
      setPosition('bottom');
    } else if (spaceAbove >= requiredSpace) {
      setPosition('top');
    } else {
      setPosition(spaceBelow > spaceAbove ? 'bottom' : 'top');
    }
  }, []);

  useEffect(() => {
    calculateOptimalPosition();

    const handleRecalculate = () => {
      calculateOptimalPosition();
    };

    window.addEventListener('scroll', handleRecalculate);
    window.addEventListener('resize', handleRecalculate);

    return () => {
      window.removeEventListener('scroll', handleRecalculate);
      window.removeEventListener('resize', handleRecalculate);
    };
  }, [calculateOptimalPosition]);

  return (
    <div
      ref={containerRef}
      className={`smart-positioned-calendar smart-positioned-calendar--${position}`}
      onFocus={calculateOptimalPosition}
    >
      <FormInputDate {...props} />
    </div>
  );
}

export function DateFieldsSection() {
  const { control } = useFormContext<AddTagFormData>();

  const todayDateString = useMemo(
    () => new Date().toISOString().split('T')[0],
    [],
  );

  return (
    <div className="rounded-xl border border-neutral-200 shadow-sm p-5 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Stack direction="col" gap="sm">
          <Controller
            name="dateSent"
            control={control}
            rules={{
              required: 'Date sent is required',
              validate: (value) => {
                if (!value || typeof value !== 'string')
                  return 'Date sent is required';

                let normalizedDate = value;
                if (value.includes('T')) {
                  [normalizedDate] = value.split('T');
                }

                const selectedDate = new Date(
                  `${normalizedDate}T00:00:00.000Z`,
                );
                if (isNaN(selectedDate.getTime())) {
                  return 'Please enter a valid date';
                }
                const todayMidnightUTC = new Date(
                  `${todayDateString}T00:00:00.000Z`,
                );

                if (selectedDate < todayMidnightUTC) {
                  return 'Date sent must be today or in the future';
                }
                return true;
              },
            }}
            render={({ field, fieldState }) => (
              <SmartPositionedFormInputDate
                label="Date Sent"
                value={field.value || ''}
                onChangeValue={field.onChange}
                notBefore={todayDateString}
                required
                error={fieldState.error?.message}
                className="w-full"
                containerClassName="w-full"
                inputId="dateSent"
              />
            )}
          />
          <P color="disabled" size="xs">
            When this will be sent to us.
          </P>
        </Stack>
        <Stack direction="col" gap="sm">
          <Controller
            name="cutDate"
            control={control}
            render={({ field, fieldState }) => (
              <SmartPositionedFormInputDate
                label="Cut Date"
                value={field.value || ''}
                onChangeValue={field.onChange}
                error={fieldState.error?.message}
                className="w-full"
                containerClassName="w-full"
                inputId="cutDate"
              />
            )}
          />
        </Stack>
      </div>
    </div>
  );
}
